// 配信中ビュー(設計書 §3.4.4)
// Phase 5 Step 2: VDO Ninja の push UI を iframe で表示し、別タブの view URL で
// 受信できることを動作確認可能な状態にする(JWT 統合は Step 4)。

import { useEffect, useState } from 'react';
import { ApiClientError } from '../../api/client';
import {
  SESSION_STATE_LABELS,
  useStopSession,
  type SessionDetail,
} from '../../api/sessions';
import { useStreamingProfiles } from '../../api/streaming-profiles';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { VdoPushFrame } from '../../components/VdoPushFrame';
import type { CaptureSettings } from '../../lib/media-capture';
import { buildVdoPushUrl, buildVdoViewUrl } from '../../lib/vdo-url';
import type { VdoSessionParams } from './StartSessionForm';

interface LiveSessionViewProps {
  session: SessionDetail;
  settings: CaptureSettings;
  vdoParams: VdoSessionParams | null;
  onSessionEnded: () => void;
}

const VDO_FRONTEND_URL =
  import.meta.env.VITE_VDO_FRONTEND_URL ?? 'https://localhost:8444';
const VDO_SIGNALING_URL =
  import.meta.env.VITE_VDO_SIGNALING_URL ?? 'wss://localhost:8443';
const VDO_PASSWORD = import.meta.env.VITE_VDO_PASSWORD ?? 'safelens';

export function LiveSessionView({
  session,
  settings,
  vdoParams,
  onSessionEnded,
}: LiveSessionViewProps) {
  const stopMutation = useStopSession();
  const profilesQuery = useStreamingProfiles();
  const profile = profilesQuery.data?.find(
    (p) => p.ulid === session.streaming_profile_ulid,
  );
  const [confirming, setConfirming] = useState(false);

  const isActive = session.state === 'starting' || session.state === 'running';
  const isStopping = session.state === 'stopping';
  const isTerminal = session.state === 'completed' || session.state === 'failed';

  useEffect(() => {
    if (isTerminal) {
      onSessionEnded();
    }
  }, [isTerminal, onSessionEnded]);

  // iOS / モバイル検知: getUserMedia が user gesture 起因でしか動作しないため
  // autostart を外して、VDO Ninja の Start ボタンをユーザにタップさせる運用にする。
  // - iPad/iPhone Safari + WKWebView ベースの全 iOS ブラウザ(Brave / Chrome 等)
  // - Android のモバイルブラウザ(念のため)
  // - iPadOS 13+ は navigator.userAgent を Mac として偽装するので
  //   maxTouchPoints も補助的に見る。
  const isMobile = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) return true;
    // iPad on iPadOS 13+ pretends to be Mac
    if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return true;
    return false;
  })();

  const pushUrl = vdoParams
    ? buildVdoPushUrl({
        frontendBaseUrl: VDO_FRONTEND_URL,
        roomId: vdoParams.room_id,
        pushId: vdoParams.push_id,
        password: VDO_PASSWORD,
        signalingUrl: vdoParams.signaling_url || VDO_SIGNALING_URL,
        pushJwt: vdoParams.push_jwt,
        useScreenshare: settings.useScreenshare,
        autostart: !isMobile,
      })
    : null;

  const viewUrl = vdoParams
    ? buildVdoViewUrl({
        frontendBaseUrl: VDO_FRONTEND_URL,
        roomId: vdoParams.room_id,
        pushId: vdoParams.push_id,
        password: VDO_PASSWORD,
        signalingUrl: vdoParams.signaling_url || VDO_SIGNALING_URL,
        viewJwt: vdoParams.view_jwt,
      })
    : null;

  return (
    <AppLayout
      title="配信中"
      description={`セッション ${session.ulid}`}
      actions={
        isActive ? (
          confirming ? (
            <div style={{ display: 'flex', gap: 8, minWidth: 280 }}>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                キャンセル
              </Button>
              <Button
                variant="danger"
                disabled={stopMutation.isPending}
                onClick={async () => {
                  try {
                    await stopMutation.mutateAsync(session.ulid);
                    setConfirming(false);
                  } catch {
                    // 下で表示
                  }
                }}
              >
                {stopMutation.isPending ? '停止中…' : '停止する'}
              </Button>
            </div>
          ) : (
            <div style={{ minWidth: 160 }}>
              <Button variant="danger" onClick={() => setConfirming(true)}>
                配信を停止
              </Button>
            </div>
          )
        ) : null
      }
    >
      <Card>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 4,
          }}
        >
          <StateBadge state={session.state} />
          <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
            開始: {formatDateTime(session.started_at)}
          </span>
        </div>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: '11px',
            color: 'var(--color-text-tertiary)',
          }}
        >
          配信先プロファイル: {profile?.name ?? session.streaming_profile_ulid}
        </p>
      </Card>

      {isMobile && !settings.useScreenshare && (
        <Card>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-warning-text)' }}>
            <strong>📱 スマートフォンで配信する際の注意</strong>
            <br />
            <strong>1. ブラウザ:</strong> iPhone は <strong>Safari</strong> をご利用ください。
            Brave / Chrome / Firefox 等は内部で WebKit を使いますが、各社固有の Privacy 制限で
            カメラ/マイクの許可ダイアログが出ない / 配信できないことがあります(特に Brave は
            プライバシー保護機能で配信不可)。
            <br />
            <strong>2. 持ち方:</strong> <strong>縦持ち(portrait)で固定</strong>してください。
            9:16 の縦長映像で配信先(FC2 ライブ等)に送出されます(プラットフォーム側で
            左右に黒帯が表示されます)。
            <br />
            ※ 配信先で 16:9 横長(フル画面)にしたい場合は、PC + 外付けカメラでの配信を
            ご利用ください。
            <br />
            <strong>3. 開始操作:</strong> 配信開始後、画面下の「Start」ボタンをタップしてカメラ /
            マイク許可を与えてください(iOS は user gesture 必須のため自動開始しません)。
            <br />
            <strong>4. 自己 view の見え方:</strong> 配信者自身の画面では「カメラから見た素の映像
            (非鏡像)」が表示されます。配信先・視聴者にも非鏡像のまま届きます(看板や服の
            文字が正しく読める向き)。
            <br />
            <strong>5. 横持ち時の見え方:</strong> iPhone を横向きに倒すと、自己 view は
            <strong>横倒し(sideways)で表示</strong>されます。これは <strong>配信先(FC2 ライブ等)
            に実際に届く映像と同じ崩れ方</strong>で、配信者がリアルタイムに「縦に戻さなきゃ」
            と気づける WYSIWYG 表示です。横持ちで配信し続けると視聴者にも sideways な映像が
            届くので、必ず縦に戻してから配信してください。
          </p>
        </Card>
      )}

      {pushUrl ? (
        <Card
          title="配信(VDO Ninja push)"
          description="iframe 内の「Share your Camera」「Join Room with Camera」等を押して配信を開始してください。"
        >
          <VdoPushFrame src={pushUrl} />
        </Card>
      ) : (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-warning-text)' }}>
            シグナリング情報が見つかりません。ブラウザリロード後はセッションの
            VDO Ninja 接続情報が失われるため、一度配信を停止し再度開始してください。
          </p>
        </Card>
      )}

      {viewUrl && (
        <Card
          title="視聴 URL(動作確認用)"
          description="別タブやモザイクアプリの WebView で開くと同じ映像が視聴できます。"
        >
          <ViewUrlBlock url={viewUrl} />
        </Card>
      )}

      {isStopping && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-warning-text)' }}>
            停止処理中…エージェントの完了応答を待っています。
          </p>
        </Card>
      )}

      {session.state === 'failed' && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            配信が失敗しました: {session.error_message ?? '理由不明'}
          </p>
        </Card>
      )}

      {stopMutation.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {stopMutation.error instanceof ApiClientError
              ? stopMutation.error.message
              : '停止に失敗しました'}
          </p>
        </Card>
      )}
    </AppLayout>
  );
}

function ViewUrlBlock({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard 失敗時は何もしない(URL が表示されているのでユーザーが手動コピー可能)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <code
        style={{
          flex: 1,
          fontSize: '10.5px',
          color: 'var(--color-text-secondary)',
          background: 'rgba(0,0,0,0.3)',
          padding: '8px 10px',
          borderRadius: 'var(--radius-sm)',
          wordBreak: 'break-all',
          fontFamily: 'monospace',
        }}
      >
        {url}
      </code>
      <div style={{ minWidth: 90 }}>
        <Button
          variant="secondary"
          onClick={copy}
          style={{ padding: '6px 10px', fontSize: '11px' }}
        >
          {copied ? 'コピー済' : 'コピー'}
        </Button>
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: SessionDetail['state'] }) {
  const color =
    state === 'running'
      ? { fg: 'var(--color-success-text)', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)' }
      : state === 'starting' || state === 'stopping'
        ? { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.3)' }
        : state === 'failed'
          ? { fg: 'var(--color-error-text)', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)' }
          : { fg: 'var(--color-text-secondary)', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)' };
  return (
    <span
      style={{
        fontSize: '11px',
        fontWeight: 500,
        color: color.fg,
        background: color.bg,
        border: `0.5px solid ${color.border}`,
        padding: '3px 10px',
        borderRadius: '4px',
      }}
    >
      {SESSION_STATE_LABELS[state]}
    </span>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
}
